import { defineStore } from 'pinia';
import { addProductGroupAPI, getAllProductGroupAPI, deleteProductGroupAPI, updateProductGroupAPI } from '@/utils/api';

export interface NavigationLinksItem {
    id: string;
    name: string;
    appearance: Appearance;
}

export interface NavigationLinks {
    departments: NavigationLinksItem[],
    productGroups: NavigationLinksItem[],
}

export interface Appearance {
    image: string;
    buttonColor: string;
    fontSizes: string;
}

export interface ViviposProductGroup {
    MID: number;
    ID: string;
    Name?: string | null;
    Description?: string | null;
    Created?: number | null;
    Modified?: number | null;
    ButtonColor?: string | null;
    FontSize?: string | null;
    Visible?: boolean | null;
    DisplayOrder?: number | null;
    Routing?: boolean | null;
    LinkDepartment?: string | null;
    LinkGroup?: string | null;
    NonDiscountable?: boolean | null;
    NonSurchargeable?: boolean | null;
    PicThumb?: string | null;
    AltLanguage?: string | null;
}

export interface ProductGroup {
    id: string;
    name: string;
    showOnMenu: boolean;
    displayOrder: number;
    usedToRouteOrders: boolean;
    nonDiscountable: boolean;
    nonSurchargeable: boolean;
    appearance: Appearance;
    navigationLinks: NavigationLinks;
    altDisplayLang: String[];
}

export function productGroupToViviposProductGroup(pg: ProductGroup): ViviposProductGroup {
    return {
        MID: 1,
        ID: pg.id,
        Name: pg.name,
        Description: '',
        Created: Date.now(),
        Modified: Date.now(),
        ButtonColor: pg.appearance.buttonColor,
        FontSize: pg.appearance.fontSizes,
        Visible: pg.showOnMenu,
        DisplayOrder: pg.displayOrder,
        Routing: pg.usedToRouteOrders,
        LinkDepartment: pg.navigationLinks.departments.map(d => d.id).join(','),
        LinkGroup: pg.navigationLinks.productGroups.map(g => g.id).join(','),
        NonDiscountable: pg.nonDiscountable,
        NonSurchargeable: pg.nonSurchargeable,
        PicThumb: pg.appearance.image,
        AltLanguage: pg.altDisplayLang ? pg.altDisplayLang.join('||') : ''
    };
}

export function viviposProductGroupToProductGroup(vpg: ViviposProductGroup): ProductGroup {
    return {
        id: vpg.ID,
        name: vpg.Name || '',
        showOnMenu: vpg.Visible || false,
        displayOrder: vpg.DisplayOrder || 0,
        usedToRouteOrders: vpg.Routing || false,
        nonDiscountable: vpg.NonDiscountable || false,
        nonSurchargeable: vpg.NonSurchargeable || false,
        appearance: {
            image: vpg.PicThumb || '',
            buttonColor: vpg.ButtonColor || '',
            fontSizes: vpg.FontSize || '',
        },
        navigationLinks: {
            departments: vpg.LinkDepartment
                ? vpg.LinkDepartment.split(',').filter(Boolean).map(id => ({
                    id,
                    name: '',
                    appearance: { image: '', buttonColor: '', fontSizes: '' }
                }))
                : [],
            productGroups: vpg.LinkGroup
                ? vpg.LinkGroup.split(',').filter(Boolean).map(id => ({
                    id,
                    name: '',
                    appearance: { image: '', buttonColor: '', fontSizes: '' }
                }))
                : [],
        },
        altDisplayLang: vpg.AltLanguage ? vpg.AltLanguage.split('||') : [],
    };
}

export const defaultProductGroup = {
    id: '',
    name: '',
    showOnMenu: false,
    displayOrder: 0,
    nonDiscountable: false,
    nonSurchargeable: false,
    usedToRouteOrders: false,
    appearance: {
        image: '',
        buttonColor: '#dddddd',
        fontSizes: 'Medium'
    },
    navigationLinks: {
        departments: [],
        productGroups: []
    },
    altDisplayLang: []
} as ProductGroup;

export const useProductGroupsStore = defineStore('productGroups', {
    state: () => ({
        productGroupList: [] as ProductGroup[],
        activeProductGroup: { ...defaultProductGroup },
        tempActiveProductGroup: { ...defaultProductGroup }
    }),
    getters: {
        getActiveProductGroup: (state) => {
            return state.activeProductGroup;
        },
        getActiveProductGroupId: (state) => {
            return state.activeProductGroup.id;
        },
        findProductGroupById: (state) => (id: string) => {
            return state.productGroupList.find(productGroup => productGroup.id === id);
        },
        getProductGroupsList: (state) => {
            return state.productGroupList;
        },
        getTempActiveProductGroup: (state) => {
            return state.tempActiveProductGroup;
        },
        getIsActiveProductGroupChanged: (state) => {
            return JSON.stringify(state.activeProductGroup) !== JSON.stringify(state.tempActiveProductGroup);
        }
    },
    actions: {
        initializeProductGroups() {
            getAllProductGroupAPI().then((productGroups: ProductGroup[]) => {
                this.productGroupList = productGroups;
            }).catch((error: Error) => {
                console.error('Error fetching categories:', error);
            });
        },
        addProductGroup(productGroup: ProductGroup) {
            addProductGroupAPI(productGroup).then((response) => {
                getAllProductGroupAPI().then((productGroups: ProductGroup[]) => {
                    this.productGroupList = productGroups;
                    this.activeProductGroup = productGroup;
                    this.tempActiveProductGroup = productGroup;
                }).catch((error: Error) => {
                    console.error('Error fetching categories:', error);
                });
            }).catch((error: Error) => {
                console.error('Error adding product group:', error);
            });
        },
        removeProductGroup(id: string) {
            deleteProductGroupAPI(id).then(() => {
                getAllProductGroupAPI().then((productGroups: ProductGroup[]) => {
                    this.productGroupList = productGroups;
                    this.activeProductGroup = { ...defaultProductGroup };
                    this.tempActiveProductGroup = { ...defaultProductGroup };
                }).catch((error: Error) => {
                    console.error('Error fetching categories:', error);
                });
            }).catch((error: Error) => {
                console.error('Error deleting product group:', error);
            });
        },
        updateProductGroup(updatedProductGroup: ProductGroup) {
            updateProductGroupAPI(updatedProductGroup).then(() => {
                getAllProductGroupAPI().then((productGroups: ProductGroup[]) => {
                    this.productGroupList = productGroups;
                    this.activeProductGroup = updatedProductGroup;
                    this.tempActiveProductGroup = updatedProductGroup;
                }).catch((error: Error) => {
                    console.error('Error fetching categories:', error);
                });
            }
            ).catch((error: Error) => {
                console.error('Error updating product group:', error);
            });
        },
        setActiveProductGroup(productGroup: ProductGroup) {
            this.activeProductGroup = productGroup;
        },
        setTempActiveProductGroup(productGroup: ProductGroup) {
            this.tempActiveProductGroup = productGroup;
        },
        initActiveProductGroup(productGroup: ProductGroup) {
            this.activeProductGroup = productGroup;
            this.tempActiveProductGroup = productGroup;
        }
    },
});